import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  PasswordInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { apiFetch, apiPost, type ApiError } from "../../api/client.js";
import { QuotaBar } from "./quota-bar.js";
import { formatBytes } from "./containerUtils.js";

interface ContainerTemplate {
  id: string;
  display_name: string;
  size_bytes?: number;
  present: boolean;
}

interface ContainerOptions {
  defaults: { cores: number; memory_mb: number; swap_mb: number; disk_gb: number };
  options: {
    cores: number[];
    memory_mb: number[];
    swap_mb: number[];
    disk_gb: number[];
  };
  templates: ContainerTemplate[];
  quota: { cpu_cores: number; ram_mb: number; disk_gb: number };
  usage: { cpu_cores: number; ram_mb: number; disk_gb: number } | null;
  usage_unavailable?: boolean;
  remaining: { cpu_cores: number; ram_mb: number; disk_gb: number } | null;
}

const NAME_RE = /^[a-zA-Z]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

interface CreateContainerModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateContainerModal({ opened, onClose, onCreated }: CreateContainerModalProps) {
  const [options, setOptions] = useState<ContainerOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [templateId, setTemplateId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [cores, setCores] = useState<number | "">("");
  const [memoryMb, setMemoryMb] = useState<number | "">("");
  const [swapMb, setSwapMb] = useState<number | "">("");
  const [diskGb, setDiskGb] = useState<number | "">("");
  const [password, setPassword] = useState("");
  const [sshPublicKeys, setSshPublicKeys] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [sshTouched, setSshTouched] = useState(false);
  const [memUnit, setMemUnit] = useState<"mb" | "gb">("gb");
  const [swapUnit, setSwapUnit] = useState<"mb" | "gb">("gb");
  const [issues, setIssues] = useState<Record<string, string>>({});

  async function loadOptions() {
    setLoading(true);
    try {
      const data = await apiFetch<ContainerOptions>("/api/container-options");
      setOptions(data);
      setCores(data.defaults.cores);
      setMemoryMb(data.defaults.memory_mb);
      setSwapMb(data.defaults.swap_mb);
      setDiskGb(data.defaults.disk_gb);
      setTemplateId(null);
    } catch (err) {
      notifications.show({ color: "red", message: friendly(err, "Could not load create options.") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (opened) {
      setName("");
      setPassword("");
      setSshPublicKeys("");
      setPasswordTouched(false);
      setSshTouched(false);
      setIssues({});
      setMemUnit("gb");
      setSwapUnit("gb");
      void loadOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const usageUnavailable = !!options?.usage_unavailable;
  const remaining = options?.remaining ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };
  const quota = options?.quota ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };

  const reqCores = typeof cores === "number" ? cores : 0;
  const reqRam = (typeof memoryMb === "number" ? memoryMb : 0) + (typeof swapMb === "number" ? swapMb : 0);
  const reqDisk = typeof diskGb === "number" ? diskGb : 0;

  const overCores = reqCores > quota.cpu_cores;
  const overRam = reqRam > remaining.ram_mb;
  const overDisk = reqDisk > remaining.disk_gb;
  const nameInvalid = name.length > 0 && !NAME_RE.test(name);

  const canSubmit =
    !submitting &&
    !usageUnavailable &&
    !!templateId &&
    NAME_RE.test(name) &&
    typeof cores === "number" &&
    typeof memoryMb === "number" &&
    typeof swapMb === "number" &&
    typeof diskGb === "number" &&
    !overCores &&
    !overRam &&
    !overDisk &&
    (password.trim().length > 0 || sshPublicKeys.trim().length > 0);

  const authMissing = password.trim().length === 0 && sshPublicKeys.trim().length === 0;
  const authError = authMissing && (passwordTouched || sshTouched)
    ? "Provide a root password or at least one SSH public key."
    : undefined;

  const templateSelectData = useMemo(() => {
    const list = options?.templates ?? [];
    return list.map((t) => ({
      value: t.id,
      label:
        typeof t.size_bytes === "number"
          ? `${t.display_name} (${formatBytes(t.size_bytes)})`
          : t.display_name,
    }));
  }, [options]);

  async function submit() {
    if (!canSubmit || typeof cores !== "number" || typeof memoryMb !== "number" || typeof swapMb !== "number" || typeof diskGb !== "number" || !templateId) return;
    setSubmitting(true);
    setIssues({});
    const body: Record<string, unknown> = {
      name,
      template_id: templateId,
      cores,
      memory_mb: memoryMb,
      swap_mb: swapMb,
      disk_gb: diskGb,
    };
    if (password.trim()) body.password = password;
    if (sshPublicKeys.trim()) body.ssh_public_keys = sshPublicKeys;
    try {
      const res = await apiPost<{ vmid: number; name: string } & Record<string, unknown>>(
        "/api/containers",
        body
      );
      notifications.show({ color: "green", message: `Container ${res.name} created (vmid ${res.vmid}).` });
      onCreated();
      onClose();
    } catch (err) {
      const e = err as ApiError;
      const detail = e.details as Record<string, unknown> | undefined;
      if (e.status === 409 && e.error === "quota_exceeded") {
        const excess = detail?.excess as Record<string, number> | undefined;
        const fields: string[] = [];
        if (excess?.cpu_cores) fields.push(`CPU +${excess.cpu_cores}`);
        if (excess?.ram_mb) fields.push(`RAM +${excess.ram_mb} MB`);
        if (excess?.disk_gb) fields.push(`Disk +${excess.disk_gb} GB`);
        notifications.show({
          color: "red",
          message: `Not enough quota — ${fields.join(", ") || "insufficient headroom"}.`,
        });
      } else if (e.status === 409 && e.error === "no_vmid_prefix") {
        notifications.show({ color: "red", message: "Your account isn't set up for VM creation yet. Contact support." });
      } else if (e.status === 502 && e.error === "vmid_range_exhausted") {
        notifications.show({ color: "red", message: "Your VMID range is full. Contact support." });
      } else if (e.status === 400 && e.error === "unknown_template") {
        notifications.show({ color: "red", message: "Template no longer available. Refresh and pick another." });
      } else if (e.status === 429) {
        notifications.show({ color: "red", message: "Too many creations. Wait a minute." });
      } else if (e.status === 502 && (e.error === "pve_unavailable" || e.error === "create_failed")) {
        notifications.show({ color: "red", message: "Proxmox error during create. Try again or contact support." });
      } else if (e.status === 400 && e.error === "invalid_request") {
        const fieldIssues: Record<string, string> = {};
        const raw = detail?.issues as Array<{ path: string[]; message: string }> | undefined;
        if (raw) {
          for (const i of raw) {
            const key = i.path[0] ?? "_";
            fieldIssues[key] = i.message;
          }
        }
        setIssues(fieldIssues);
        notifications.show({ color: "red", message: "Some fields need attention." });
      } else {
        notifications.show({ color: "red", message: friendly(err, "Create failed.") });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const memDisplay =
    memUnit === "gb" && typeof memoryMb === "number" ? memoryMb / 1024 : memoryMb;
  const swapDisplay =
    swapUnit === "gb" && typeof swapMb === "number" ? swapMb / 1024 : swapMb;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Create container"
      size="lg"
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
    >
      <Stack gap="md">
        {loading && (
          <Text c="dimmed" size="sm">
            Loading options…
          </Text>
        )}

        {options && usageUnavailable && (
          <Alert color="yellow" variant="light" p="sm">
            Can&apos;t compute current usage (Proxmox unreachable). Please try again later.
          </Alert>
        )}

        {options && !usageUnavailable && (
          <>
            <Alert color="gray" variant="light" p="sm">
              <Stack gap="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Quota remaining
                </Text>
                <QuotaBar label="RAM (memory + swap)" used={quota.ram_mb - remaining.ram_mb} total={quota.ram_mb} unit="MB" />
                <QuotaBar label="Disk" used={quota.disk_gb - remaining.disk_gb} total={quota.disk_gb} unit="GB" />
              </Stack>
            </Alert>

            <Select
              label="Template"
              description="OS template to clone. Required."
              placeholder="Pick a template"
              value={templateId}
              onChange={setTemplateId}
              data={templateSelectData}
              searchable
              error={issues.template_id}
              disabled={submitting}
            />

            <TextInput
              label="Name"
              description="Hostname for the container."
              placeholder="my-container"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              error={nameInvalid ? "Must start with a letter. Letters, digits, and hyphens only (no underscores, no trailing hyphen). 1–63 chars." : issues.name}
              disabled={submitting}
            />

            <Group grow align="flex-start">
              <Select
                label="CPU cores"
                value={typeof cores === "number" ? String(cores) : null}
                onChange={(v) => setCores(v ? Number(v) : "")}
                data={(options.options.cores ?? []).filter((n) => n <= quota.cpu_cores).map((n) => ({ value: String(n), label: String(n) }))}
                error={overCores ? `Up to ${quota.cpu_cores} cores per container.` : issues.cores}
                disabled={submitting}
              />
              <Select
                label="Disk (GB)"
                value={typeof diskGb === "number" ? String(diskGb) : null}
                onChange={(v) => setDiskGb(v ? Number(v) : "")}
                data={(options.options.disk_gb ?? []).map((n) => ({ value: String(n), label: String(n) }))}
                error={overDisk ? `Only ${remaining.disk_gb} GB remaining.` : issues.disk_gb}
                disabled={submitting}
              />
            </Group>

            <Stack gap={4}>
              <Group justify="space-between" align="flex-end">
                <Box style={{ flex: 1 }}>
                  <NumberInput
                    label={memUnit === "gb" ? "Memory (GB)" : "Memory (MB)"}
                    value={memDisplay}
                    onChange={(v) => {
                      const n = typeof v === "number" ? v : 0;
                      setMemoryMb(memUnit === "gb" ? Math.round(n * 1024) : n);
                    }}
                    min={memUnit === "gb" ? 1 : 128}
                    max={
                      memUnit === "gb"
                        ? Math.min(256, Math.floor(remaining.ram_mb / 1024) || 1)
                        : Math.min(262144, Math.max(128, remaining.ram_mb))
                    }
                    error={overRam ? `Only ${remaining.ram_mb} MB RAM quota remaining.` : issues.memory_mb}
                    disabled={submitting}
                  />
                </Box>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => setMemUnit((u) => (u === "gb" ? "mb" : "gb"))}
                  disabled={submitting}
                >
                  Switch to {memUnit === "gb" ? "MB" : "GB"}
                </Button>
              </Group>
            </Stack>

            <Stack gap={4}>
              <Group justify="space-between" align="flex-end">
                <Box style={{ flex: 1 }}>
                  <NumberInput
                    label={swapUnit === "gb" ? "Swap (GB)" : "Swap (MB)"}
                    value={swapDisplay}
                    onChange={(v) => {
                      const n = typeof v === "number" ? v : 0;
                      setSwapMb(swapUnit === "gb" ? Math.round(n * 1024) : n);
                    }}
                    min={0}
                    max={
                      swapUnit === "gb"
                        ? Math.min(256, Math.floor(remaining.ram_mb / 1024))
                        : Math.min(262144, Math.max(0, remaining.ram_mb))
                    }
                    error={issues.swap_mb}
                    disabled={submitting}
                  />
                </Box>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => setSwapUnit((u) => (u === "gb" ? "mb" : "gb"))}
                  disabled={submitting}
                >
                  Switch to {swapUnit === "gb" ? "MB" : "GB"}
                </Button>
              </Group>
            </Stack>

            <Text size="xs" c="dimmed">Provide a root password OR at least one SSH public key (both may be used).</Text>

            <PasswordInput
              label="Root password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              onBlur={() => setPasswordTouched(true)}
              error={issues.password || authError}
              disabled={submitting}
            />

            <Textarea
              label="SSH public keys"
              description="One key per line, OpenSSH format. Added to /root/.ssh/authorized_keys."
              value={sshPublicKeys}
              onChange={(e) => setSshPublicKeys(e.currentTarget.value)}
              onBlur={() => setSshTouched(true)}
              autosize
              minRows={2}
              maxRows={6}
              error={issues.ssh_public_keys || authError}
              disabled={submitting}
            />

            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={() => void submit()} loading={submitting} disabled={!canSubmit}>
                Create container
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

function friendly(err: unknown, fallback: string): string {
  const e = err as ApiError | undefined;
  return e?.message || fallback;
}
